/**
 * Pure helpers behind the "Add trajectory from LangSmith" door on the
 * Trajectories page (TRA-1087, path 2). Kept free of React and the API
 * client so the decisions are unit-testable in the node vitest setup.
 */

import type {
  TrajectoryImportRequestExport,
  TrajectoryImportResponse,
  TrajectoryImportReview,
} from '@/api/generated/model'
import { isRecord } from '@/lib/guards'
import { conversationPath } from '@/components/traces/conversationLink'

export type UploadVerdict = 'bad' | 'good'

/**
 * LangSmith run ids are UUIDs. The upload door only makes sense when the
 * search box holds one: a miss on a plain word is a filter miss, not a run
 * the platform never recorded. Version-blind on purpose: LangSmith mints
 * v7 ids.
 */
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const UUID_PATTERN = new RegExp(`^${UUID}$`, 'i')

/** A LangSmith link, split into its path and query. */
const LANGSMITH_LINK = /^https?:\/\/smith\.langchain\.com(?::\d+)?(\/[^?#]*)?(?:\?([^#]*))?(?:#.*)?$/i
/**
 * Where a LangSmith link names its trace: `/trace/<id>` in the path when
 * the trace is opened on its own, `peeked_trace=<id>` or `trace_id=<id>` in
 * the query when it is opened from a project's run list, or `/r/<id>` in
 * the path, the run link Cartage's test runner prints, which names the root
 * run (its id is the trace id). The `/run/<id>` after `/trace/` is one step
 * inside the trace; the client never sends step ids, so only the trace id
 * can be found.
 */
const TRACE_IN_PATH = new RegExp(`/trace/(${UUID})(?:/|$)`, 'i')
const TRACE_IN_QUERY = new RegExp(`(?:^|&)(?:peeked_trace|trace_id)=(${UUID})(?:&|$)`, 'i')
const RUN_IN_PATH = new RegExp(`/r/(${UUID})(?:/|$)`, 'i')

/**
 * The id a search names: a bare UUID as typed, or the trace id (lowercased)
 * of a pasted LangSmith link. The same rule as the api's `run_id_of`
 * (TRA-1541), so the web and the server agree on what is an id search.
 */
export function langsmithTraceIdOf(text: string): string | null {
  const trimmed = text.trim()
  if (UUID_PATTERN.test(trimmed)) return trimmed
  const link = LANGSMITH_LINK.exec(trimmed)
  if (link === null) return null
  const path = link[1] ?? ''
  const query = link[2] ?? ''
  const found = TRACE_IN_PATH.exec(path) ?? TRACE_IN_QUERY.exec(query) ?? RUN_IN_PATH.exec(path)
  const id = found?.[1]
  return id === undefined ? null : id.toLowerCase()
}

/**
 * Where to send the user once the run is on the platform: the conversation
 * door when the gateway gave one (every import does), else the trace key,
 * else nowhere (a server bug — the caller shows the result in place).
 */
export function importedRunUrl(result: TrajectoryImportResponse): string | null {
  if (result.conversation_id) {
    return conversationPath(result.conversation_id)
  }
  if (result.trace_key) {
    return `/conversations?trace_key=${encodeURIComponent(result.trace_key)}`
  }
  return null
}

/**
 * The uploader's review, as the api wants it, or null when they left the
 * review empty (the api then flags the run for the review queue). The
 * verdict pills default to nothing; a comment with no pill picked is a
 * complaint, so it goes up as Bad. A Bad verdict with no words is refused
 * here with the sentence the dialog shows, because the eval pipeline turns
 * the reason into a test and has nothing to work with otherwise.
 */
export function uploadReview(
  verdict: UploadVerdict | null,
  comment: string,
): { kind: 'review'; review: TrajectoryImportReview | null } | { kind: 'error'; message: string } {
  const notes = comment.trim()
  if (verdict === null && notes === '') return { kind: 'review', review: null }
  const approved = verdict === 'good'
  if (!approved && notes === '') {
    return { kind: 'error', message: 'Say what is wrong with the run, or leave the review empty.' }
  }
  return { kind: 'review', review: { approved, notes } }
}

/** One sentence for the toast after a successful upload. */
export function importedRunMessage(result: TrajectoryImportResponse): string {
  const turns = result.turns === 1 ? '1 model call' : `${String(result.turns)} model calls`
  const added = result.imported
    ? `Trajectory added (${turns}).`
    : `This run was already on the platform (${turns}).`
  return `${added} ${reviewOutcomeMessage(result.review_status ?? null)}`
}

/**
 * Where the run went next, for the toast. Dismissed and held come back
 * only from a repeat upload of a run whose flag was already settled or
 * parked: the api left it alone, so the toast must not promise the review
 * queue. Null means the api stored the run but could not write its label
 * (logged server-side): say so, and point at the fix, instead of letting
 * the run sit unflagged in silence.
 */
export function reviewOutcomeMessage(status: TrajectoryImportResponse['review_status']): string {
  if (status === 'reviewed') return 'Your review is saved and the run feeds evals.'
  if (status === 'unreviewed') return 'It is flagged and waits in the review queue.'
  if (status === 'dismissed') {
    return 'Its flag was dismissed earlier, so it stays out of the review queue.'
  }
  if (status === 'held') return 'Its flag is on hold and returns to the review queue on a later night.'
  return 'It could not be flagged. Flag it on its row so it reaches review.'
}

/**
 * Client-side gate before the file is read: a JSON file, not empty.
 * Returns the message to show, or null when the file may be opened. The
 * file's size on disk is not a reason to refuse: a batch run's export is
 * mostly wrapper steps that `slimExport` drops before the upload, so the
 * size that matters is measured after trimming (`slimmedUploadProblem`).
 */
export function uploadProblem(file: { name: string; size: number } | null): string | null {
  if (file === null) return 'Pick the exported JSON file first.'
  if (file.size === 0) return 'That file is empty.'
  if (!/\.json$/i.test(file.name)) {
    return 'LangSmith exports are .json files. Pick the exported JSON.'
  }
  return null
}

/**
 * The api's cap on one upload body. A trimmed export is nearly all model
 * calls, so this now bounds the model calls alone — the same bytes the
 * gateway would have stored had the run gone through it live.
 */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024

/** Room for the request's other fields (run id, review) around the export. */
const UPLOAD_ENVELOPE_BYTES = 64 * 1024

/**
 * After trimming: the bytes the export will take on the wire, against the
 * api's cap. Returns the message to show, or null when it fits.
 */
export function slimmedUploadProblem(exportBytes: number): string | null {
  if (exportBytes + UPLOAD_ENVELOPE_BYTES <= MAX_UPLOAD_BYTES) return null
  const mb = (exportBytes / (1024 * 1024)).toFixed(0)
  return `The model calls in this run are ${mb} MB. One upload takes up to 25 MB. Ask TrainLoop to raise the limit.`
}

/** UTF-8 size of the export as the request will carry it. */
export function exportByteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length
}

/** The run types whose bodies the gateway turns into stored model calls. */
const MODEL_CALL_RUN_TYPES = new Set(['llm', 'chat_model'])

function runType(run: { [key: string]: unknown }): string | null {
  if (typeof run.run_type === 'string') return run.run_type
  const ls = run.langsmith
  if (isRecord(ls) && typeof ls.run_type === 'string') return ls.run_type
  return null
}

/**
 * Drop what the gateway never stores, so a big export fits one upload.
 *
 * A LangGraph trace repeats the full message list in every wrapper step
 * (chains, channel writes, branches), and those repeats are most of a
 * batch run's export — a 66 MB file whose 39 model calls are 12 MB. The
 * gateway keeps only the model calls (`llm` / `chat_model` runs) and reads
 * nothing but ids, names, types and times from the other runs, so their
 * `inputs` and `outputs` can go. Everything else stays as it was: ids,
 * parent links, `dotted_order`, errors, and the `child_runs` nesting of
 * SDK exports (walked in place). Handles the three export shapes the
 * gateway accepts: `{trace_id, runs: [...]}`, a flat list, one nested run.
 *
 * Returns the trimmed export and the number of model calls found (0 means
 * the gateway will refuse the file; its own sentence says why).
 */
export function slimExport(value: TrajectoryImportRequestExport): {
  export: TrajectoryImportRequestExport
  modelCalls: number
} {
  let modelCalls = 0
  const slimRun = (run: unknown): unknown => {
    if (!isRecord(run)) return run
    const type = runType(run)
    const keep = type !== null && MODEL_CALL_RUN_TYPES.has(type)
    if (keep) modelCalls += 1
    const out: { [key: string]: unknown } = {}
    for (const [key, field] of Object.entries(run)) {
      if (!keep && (key === 'inputs' || key === 'outputs')) continue
      out[key] = key === 'child_runs' && Array.isArray(field) ? field.map(slimRun) : field
    }
    return out
  }
  if (Array.isArray(value)) {
    return { export: value.map(slimRun), modelCalls }
  }
  if (Array.isArray(value.runs) && !('id' in value)) {
    return { export: { ...value, runs: value.runs.map(slimRun) }, modelCalls }
  }
  return { export: slimRun(value) as { [key: string]: unknown }, modelCalls }
}

/**
 * Parse the downloaded file's text. LangSmith exports are one JSON value
 * (an object or a list); anything else is the wrong file. The server does
 * the real validation — this only turns "SyntaxError" into a sentence.
 */
export type ParsedExport =
  | { kind: 'ok'; export: TrajectoryImportRequestExport }
  | { kind: 'error'; message: string }

export function parseExportFile(text: string): ParsedExport {
  let value: unknown
  try {
    value = JSON.parse(text)
  } catch {
    return {
      kind: 'error',
      message: 'That file is not JSON. In LangSmith use More actions → Download → Download trace.',
    }
  }
  if (value === null || typeof value !== 'object') {
    return { kind: 'error', message: 'That file is not a LangSmith export.' }
  }
  return { kind: 'ok', export: Array.isArray(value) ? value : (value as { [key: string]: unknown }) }
}

/**
 * Read the picked file, then parse it. The browser can refuse the read
 * (the file moved or changed after it was picked); that is one sentence
 * on the dialog, never a rejected promise the caller has to unwind.
 */
export async function readExportFile(file: { text(): Promise<string> }): Promise<ParsedExport> {
  let text: string
  try {
    text = await file.text()
  } catch {
    return { kind: 'error', message: 'Could not read that file. Pick it again.' }
  }
  return parseExportFile(text)
}
